import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a continuous, non-interactive marquee of credential cards.
 * Unlike `marquee-component`, it never pauses or slows on hover/focus — the loop is always running.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The animated row; gets a duplicated `content` appended for a seamless loop.
 * @property {HTMLElement} content - Holds the repeated item sets that fill the viewport width.
 * @property {HTMLElement[]} repeatedItems - The repeatable set of rendered blocks.
 *
 * @extends Component<Refs>
 */
class MarqueeCredentialsComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'repeatedItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { repeatedItems } = this.refs;
    if (repeatedItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  async #queryNumberOfCopies() {
    const { repeatedItems } = this.refs;

    return new Promise((resolve) => {
      if (!repeatedItems[0]) {
        setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
        return;
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: itemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: itemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(repeatedItems[0]);
    });
  }

  #handleResize = debounce(async () => {
    const { repeatedItems } = this.refs;
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) return;

    const currentNumberOfCopies = repeatedItems.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, repeatedItems } = this.refs;

    if (!repeatedItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = repeatedItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-credentials-component')) {
  customElements.define('marquee-credentials-component', MarqueeCredentialsComponent);
}
